import {
    NumberInput,
    TextInput,
    Textarea,
    Button,
    Switch,
    TagsInput,
    Box,
    useMantineColorScheme,
    ColorInput,
    ActionIcon,
    Text,
    Stack,
    Divider,
    Group,
    Select,
    Checkbox,
    Tabs,
    SimpleGrid,
} from '@mantine/core';
import { Trash, Plus, Settings2, Database, ListTodo, ShieldCheck, Palette } from 'lucide-react';
import { UseFormReturnType } from '@mantine/form';
import type { FormInput, DataSource, TableSchema, FormField } from '@bi-studio/shared/lib';
import { RoleSelector } from '@/components/common/RoleSelector';
import { BaseContainer, ConfigTabs, ConfigTabItem } from '../common';

interface FormEditViewProps {
    isSaving: boolean;
    activeTab: string | null;
    setActiveTab: (tab: string | null) => void;
    form: UseFormReturnType<FormInput>;
    dataSources: DataSource[];
    tables: TableSchema[];
    loadingDataSources: boolean;
    loadingTables: boolean;
}

export function FormEditView({
    isSaving,
    activeTab,
    setActiveTab,
    form,
    dataSources,
    tables,
    loadingDataSources,
    loadingTables,
}: FormEditViewProps) {
    const { colorScheme } = useMantineColorScheme();

    const addField = () => {
        form.insertListItem('fields', {
            name: `field_${form.values.fields.length + 1}`,
            label: `New Field ${form.values.fields.length + 1}`,
            type: 'text',
            required: false,
            order: form.values.fields.length,
        });
    };

    const tabs: ConfigTabItem[] = [
        { value: "general", label: "General", icon: <Settings2 size={14} /> },
        { value: "data", label: "Data Source", icon: <Database size={14} /> },
        { value: "fields", label: "Fields", icon: <ListTodo size={14} /> },
        { value: "access", label: "Access", icon: <ShieldCheck size={14} /> },
        { value: "style", label: "Style", icon: <Palette size={14} /> },
    ];

    return (
        <BaseContainer>
            <Box p={{ base: 'xs', sm: 'md' }}>
                <ConfigTabs
                    value={activeTab}
                    onChange={setActiveTab}
                    tabs={tabs}
                >
                    <Tabs.Panel value="general">
                        <Stack gap="md">
                            <TextInput
                                label="Form Name"
                                placeholder="My Data Entry Form"
                                description="The name used to identify this form in the workspace."
                                required
                                {...form.getInputProps('name')}
                            />
                            <Textarea
                                label="Description"
                                placeholder="Optional description of this form"
                                description="A brief explanation of what this form is for."
                                {...form.getInputProps('description')}
                            />
                            <Switch
                                label="Public Access"
                                description="Allow anyone with the link to submit this form without logging in"
                                checked={form.values.config?.isPublic as boolean}
                                onChange={(e) => form.setFieldValue('config.isPublic' as any, e.currentTarget.checked)}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="data">
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            <Select
                                label="Database"
                                placeholder={loadingDataSources ? "Loading databases..." : "Select a database source"}
                                data={dataSources.map((ds) => ({ value: ds.id, label: ds.name }))}
                                {...form.getInputProps('dataSourceId')}
                                required
                                disabled={loadingDataSources}
                                description={dataSources.length === 0 && !loadingDataSources ? "No data sources found. Please add one in settings." : "Select the target database for submissions."}
                            />
                            <Select
                                label="Target Table"
                                placeholder={loadingTables ? "Loading tables..." : "Select a table"}
                                data={tables.map((t) => ({ value: t.name, label: t.name }))}
                                {...form.getInputProps('tableName')}
                                required
                                disabled={!form.values.dataSourceId || loadingTables}
                                description="Submissions will be inserted into this table."
                            />
                        </SimpleGrid>
                    </Tabs.Panel>

                    <Tabs.Panel value="fields">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Box>
                                    <Text fw={600} size="sm">Form Field Mapping</Text>
                                    <Text size="xs" c="dimmed">
                                        Configure how form inputs map to your database columns.
                                    </Text>
                                </Box>
                                <Button
                                    variant="light"
                                    color="gray"
                                    leftSection={<Plus size={16} />}
                                    onClick={addField}
                                    size="xs"
                                >
                                    Add Custom Field
                                </Button>
                            </Group>

                            <Stack gap="sm">
                                {form.values.fields.map((field: FormField, index: number) => (
                                    <Box
                                        key={index}
                                        p="md"
                                        style={{
                                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                            borderRadius: 'var(--mantine-radius-sm)',
                                            border: '1px solid var(--modal-border)'
                                        }}
                                    >
                                        <Stack gap="xs">
                                            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                                                <TextInput
                                                    label="DB Column"
                                                    {...form.getInputProps(`fields.${index}.name`)}
                                                    disabled // Keep name synced with table
                                                />
                                                <TextInput
                                                    label="Label"
                                                    {...form.getInputProps(`fields.${index}.label`)}
                                                />
                                                <Select
                                                    label="Type"
                                                    data={[
                                                        { value: 'text', label: 'Text' },
                                                        { value: 'number', label: 'Number' },
                                                        { value: 'date', label: 'Date' },
                                                        { value: 'checkbox', label: 'Checkbox' },
                                                        { value: 'select', label: 'Dropdown' },
                                                        { value: 'multiselect', label: 'Multi Select' },
                                                    ]}
                                                    {...form.getInputProps(`fields.${index}.type`)}
                                                />
                                            </SimpleGrid>

                                            <SimpleGrid cols={{ base: 1, sm: (field.type === 'select' || field.type === 'multiselect') ? 2 : 1 }} spacing="sm">
                                                {(field.type === 'select' || field.type === 'multiselect') && (
                                                    <TagsInput
                                                        label="Options"
                                                        placeholder="Type and press Enter"
                                                        {...form.getInputProps(`fields.${index}.options`)}
                                                        clearable
                                                    />
                                                )}
                                                <Group justify="space-between" align="center" h="100%">
                                                    <Checkbox
                                                        label="Required Field"
                                                        {...form.getInputProps(`fields.${index}.required`, { type: 'checkbox' })}
                                                    />
                                                    <ActionIcon color="red" variant="subtle" onClick={() => form.removeListItem('fields', index)} size="lg">
                                                        <Trash size={18} />
                                                    </ActionIcon>
                                                </Group>
                                            </SimpleGrid>
                                        </Stack>
                                    </Box>
                                ))}

                                {form.values.fields.length === 0 && (
                                    <Box
                                        p="xl"
                                        style={{
                                            border: '1px dashed var(--modal-border)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Text c="dimmed" size="sm">
                                            Select a table in the <b>Data Source</b> tab to auto-generate fields, or add them manually.
                                        </Text>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="access">
                        <Stack gap="md">
                            <RoleSelector
                                value={form.values.allowedRoles || []}
                                onChange={(val) => form.setFieldValue('allowedRoles', val)}
                                disabled={isSaving}
                            />
                            <Divider label="Rate Limiting" labelPosition="center" />
                            <Switch
                                label="Enable Rate Limiting"
                                description="Prevent spam by limiting the number of submissions per user/device."
                                checked={!!(form.values.config?.rateLimit as any)?.enabled}
                                onChange={(e) => form.setFieldValue('config.rateLimit.enabled' as any, e.currentTarget.checked)}
                            />
                            {!!(form.values.config?.rateLimit as any)?.enabled && (
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                    <NumberInput
                                        label="Limit"
                                        description="Max allowed submissions."
                                        {...form.getInputProps('config.rateLimit.limit')}
                                        min={1}
                                    />
                                    <NumberInput
                                        label="Window (seconds)"
                                        description="Reset period for the limit."
                                        {...form.getInputProps('config.rateLimit.window')}
                                        min={1}
                                    />
                                </SimpleGrid>
                            )}
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="style">
                        <Stack gap="md">
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            <TextInput
                                label="Background Image URL"
                                placeholder="https://example.com/background.jpg"
                                description="Apply a custom background image to the form."
                                {...form.getInputProps('config.backgroundImage')}
                            />
                            <ColorInput
                                label="Background Color"
                                placeholder="Pick a fallback color"
                                description="Solid background color if no image is provided."
                                {...form.getInputProps('config.backgroundColor')}
                            />
                        </SimpleGrid>
                        </Stack>
                    </Tabs.Panel>
                </ConfigTabs>
            </Box>
        </BaseContainer>
    );
}
